import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { getTokenHolders, getTokenHolderStats, HolderData } from '../services/holderService';

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const PageTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const HoldersTable = styled.div`
  background-color: #0a0a0a;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 1rem;
  background-color: #161616;
  border-bottom: 1px solid #222;
  font-weight: bold;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 1rem;
  border-bottom: 1px solid #222;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #161616;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const Address = styled.div`
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Balance = styled.div`
  text-align: right;
  font-weight: bold;
`;

const Rank = styled.div`
  text-align: center;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.primary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Button = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: #000;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: #555;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background-color: #161616;
  border-radius: 4px;
  padding: 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  background-color: rgba(255, 85, 85, 0.2);
  color: ${props => props.theme.colors.error};
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  background-color: #161616;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 0.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  width: 100%;
  max-width: 300px;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    outline: none;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PageButton = styled.button<{ active?: boolean }>`
  background-color: ${props => props.active ? props.theme.colors.primary : '#161616'};
  color: ${props => props.active ? '#000' : props.theme.colors.text};
  border: 1px solid ${props => props.active ? 'transparent' : '#333'};
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primary : '#222'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Holders: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [holders, setHolders] = useState<HolderData[]>([]);
  const [filteredHolders, setFilteredHolders] = useState<HolderData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const holdersPerPage = 20;
  const [stats, setStats] = useState({
    totalHolders: 0,
    totalSupply: 0,
    averageHolding: 0,
    topTenPercent: 0
  });

  // Function to fetch token holders
  const fetchHolders = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const allHolders = await getTokenHolders();
      setHolders(allHolders);
      setFilteredHolders(allHolders);
      
      // Calculate stats
      const holderStats = getTokenHolderStats(allHolders);
      setStats(holderStats);
    } catch (err) {
      console.error('Error fetching holders:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch token holders');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchHolders();
    }
  }, [isAuthenticated]);
  
  useEffect(() => {
    if (searchTerm) {
      const filtered = holders.filter(holder => 
        holder.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredHolders(filtered);
      setCurrentPage(1);
    } else {
      setFilteredHolders(holders);
    }
  }, [searchTerm, holders]);
  
  // Export to CSV functionality
  const exportToCsv = () => {
    const headers = ['Rank', 'Address', 'Proportion of 100K'];
    const csvContent = [
      headers.join(','),
      ...filteredHolders.map((holder, idx) => 
        [idx + 1, holder.address, holder.proportionOf100k.toLocaleString(undefined, { maximumFractionDigits: 2 })].join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `voovo_holders_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Pagination
  const indexOfLastHolder = currentPage * holdersPerPage;
  const indexOfFirstHolder = indexOfLastHolder - holdersPerPage;
  const currentHolders = filteredHolders.slice(indexOfFirstHolder, indexOfLastHolder);
  const totalPages = Math.ceil(filteredHolders.length / holdersPerPage);
  
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  
  // Generate page numbers
  const pageNumbers: number[] = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }
  
  // Show max 5 pages with ellipsis
  const renderPageNumbers = () => {
    if (totalPages <= 7) {
      return pageNumbers.map(number => (
        <PageButton 
          key={number}
          active={number === currentPage}
          onClick={() => paginate(number)}
        >
          {number}
        </PageButton>
      ));
    }
    
    let renderedPageNumbers = [];
    
    // Always show first page
    renderedPageNumbers.push(
      <PageButton 
        key={1}
        active={1 === currentPage}
        onClick={() => paginate(1)}
      >
        1
      </PageButton>
    );
    
    // Show dots if not on first 3 pages
    if (currentPage > 3) {
      renderedPageNumbers.push(<span key="dots1">...</span>);
    }
    
    // Show current page -1 and +1
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);
    
    for (let i = startPage; i <= endPage; i++) {
      renderedPageNumbers.push(
        <PageButton 
          key={i}
          active={i === currentPage}
          onClick={() => paginate(i)}
        >
          {i}
        </PageButton>
      );
    }
    
    // Show dots if not on last 3 pages
    if (currentPage < totalPages - 2) {
      renderedPageNumbers.push(<span key="dots2">...</span>);
    }
    
    // Always show last page
    renderedPageNumbers.push(
      <PageButton 
        key={totalPages}
        active={totalPages === currentPage}
        onClick={() => paginate(totalPages)}
      >
        {totalPages}
      </PageButton>
    );
    
    return renderedPageNumbers;
  };
  
  // Only render for authenticated users (add admin check for production)
  if (!isAuthenticated) {
    return (
      <DashboardLayout activeMenuItem="console">
        <MainContent>
          <ErrorMessage>You must be logged in to view this page.</ErrorMessage>
        </MainContent>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeMenuItem="console">
      <MainContent>
        <PageTitle>VOOVO Token - 100K Distribution</PageTitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <StatsContainer>
          <StatCard>
            <StatValue>{stats.totalHolders.toLocaleString()}</StatValue>
            <StatLabel>Total Holders</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>100,000</StatValue>
            <StatLabel>Total Available</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{(100000 / stats.totalHolders).toLocaleString(undefined, { maximumFractionDigits: 2 })}</StatValue>
            <StatLabel>Average Distribution</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.topTenPercent.toLocaleString(undefined, { maximumFractionDigits: 2})}%</StatValue>
            <StatLabel>Top 10 Holders %</StatLabel>
          </StatCard>
        </StatsContainer>
        
        <ButtonGroup>
          <Button onClick={exportToCsv} disabled={isLoading || holders.length === 0}>
            Export to CSV
          </Button>
          <Button onClick={fetchHolders} disabled={isLoading}>
            Refresh Data
          </Button>
        </ButtonGroup>
        
        <Input
          type="text"
          placeholder="Search by address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        {isLoading ? (
          <LoadingIndicator>Loading holder data...</LoadingIndicator>
        ) : (
          <>
            <HoldersTable>
              <TableHeader>
                <Rank>Rank</Rank>
                <Address>Wallet Address</Address>
                <Balance>Proportion of 100K</Balance>
              </TableHeader>
              
              {currentHolders.length === 0 ? (
                <TableRow>
                  <div style={{ gridColumn: '1 / span 3', textAlign: 'center', padding: '1rem' }}>
                    No holders found
                  </div>
                </TableRow>
              ) : (
                currentHolders.map((holder, idx) => (
                  <TableRow key={holder.address}>
                    <Rank>{indexOfFirstHolder + idx + 1}</Rank>
                    <Address title={holder.address}>
                      {holder.address}
                    </Address>
                    <Balance>{holder.proportionOf100k.toLocaleString(undefined, { maximumFractionDigits: 2 })}</Balance>
                  </TableRow>
                ))
              )}
            </HoldersTable>
            
            {filteredHolders.length > holdersPerPage && (
              <Pagination>
                <PageButton
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </PageButton>
                
                {renderPageNumbers()}
                
                <PageButton
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </PageButton>
              </Pagination>
            )}
          </>
        )}
      </MainContent>
    </DashboardLayout>
  );
};

export default Holders; 